import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";

import { marketplaceTitle } from "@/api/returns";
import * as sync from "@/api/stock_sync";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { extractErrorMessage } from "@/lib/api-errors";
import { LoadState } from "@/lib/scan-ui/screen";

export const STOCK_SYNC_WAREHOUSES_KEY = ["stock-sync-warehouses"] as const;

export function stockSyncWarehouseUrl(marketplace: string, warehouseKey: string): string {
  return `/settings/fbs/stock/${encodeURIComponent(marketplace)}/${encodeURIComponent(warehouseKey)}`;
}

function rowKey(w: { marketplace: string; warehouse_key: string }): string {
  return `${w.marketplace}:${w.warehouse_key}`;
}

/**
 * Склады маркетплейсов в синке: включатель и сколько товаров отмечено.
 *
 * Выключенный склад синк не трогает вовсе (ни числа, ни нули), но его спрос
 * вычитается. Включить с пустым выбором бэкенд не даёт (409) — текст отказа
 * стоит у строки склада, пока его не сменит следующее действие: тост
 * исчезал бы раньше, чем его прочтут.
 */
export function StockSyncWarehousesCard(): React.ReactElement {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: STOCK_SYNC_WAREHOUSES_KEY, queryFn: sync.warehouses });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const toggle = useMutation({
    mutationFn: (v: { marketplace: string; warehouse_key: string; enabled: boolean }) =>
      sync.switchWarehouse(v.marketplace, v.warehouse_key, v.enabled),
    onMutate: (v) =>
      setErrors((prev) => {
        const next = { ...prev };
        delete next[rowKey(v)];
        return next;
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: STOCK_SYNC_WAREHOUSES_KEY }),
    onError: (err, v) =>
      setErrors((prev) => ({
        ...prev,
        [rowKey(v)]: extractErrorMessage(err, "Не удалось переключить склад"),
      })),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Склады маркетплейсов</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          На включённом складе публикуется остаток только отмеченных товаров, у остальных — 0.
          Остатки выключенного склада синхронизация не изменяет, но его заказы уменьшают
          свободный остаток склада 1С.
        </p>
        {q.isLoading || q.error || !q.data ? (
          <LoadState
            isLoading={q.isLoading}
            error={q.error}
            onRetry={() => void q.refetch()}
            className="flex flex-col items-center gap-3 p-4 text-center"
          />
        ) : (
          <>
            {q.data.warehouses.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Нет складов: у активных кабинетов не задан склад 1С.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="py-1 pr-2">Маркетплейс</th>
                      <th className="pr-2">Кабинет</th>
                      <th className="pr-2">Склад маркетплейса</th>
                      <th className="pr-2">Склад 1С</th>
                      <th className="pr-2">Включён</th>
                      <th className="pr-2">Товары</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.warehouses.map((w) => {
                      const key = rowKey(w);
                      const err = errors[key];
                      const busy =
                        toggle.isPending && toggle.variables !== undefined &&
                        rowKey(toggle.variables) === key;
                      return (
                        <React.Fragment key={key}>
                          <tr className="border-t align-top">
                            <td className="py-1 pr-2">{marketplaceTitle(w.marketplace)}</td>
                            <td className="pr-2">{w.cabinet_name}</td>
                            <td className="pr-2">
                              {w.warehouse_name}
                              <div className="text-xs text-muted-foreground">{w.warehouse_key}</div>
                            </td>
                            <td className="pr-2">{w.one_c_warehouse_name ?? "—"}</td>
                            <td className="pr-2">
                              <input
                                type="checkbox"
                                aria-label={`Склад ${w.warehouse_name} включён`}
                                checked={w.enabled}
                                disabled={busy}
                                onChange={(e) =>
                                  toggle.mutate({
                                    marketplace: w.marketplace,
                                    warehouse_key: w.warehouse_key,
                                    enabled: e.target.checked,
                                  })
                                }
                              />
                            </td>
                            <td className="pr-2 tabular-nums">
                              {w.units_count === null
                                ? `отмечено ${w.selected_count}, всего — неизвестно (откройте склад)`
                                : `отмечено ${w.selected_count} из ${w.units_count}`}
                            </td>
                            <td>
                              <Link
                                className="text-primary underline"
                                to={stockSyncWarehouseUrl(w.marketplace, w.warehouse_key)}
                              >
                                Открыть
                              </Link>
                            </td>
                          </tr>
                          {err && (
                            <tr>
                              <td colSpan={7} className="pb-2">
                                <div
                                  role="alert"
                                  className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive"
                                >
                                  <AlertTriangle className="mr-1 inline size-4" />
                                  {err}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {q.data.notes.length > 0 && (
              <ul className="list-disc pl-5 text-xs text-muted-foreground">
                {q.data.notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
